import { useEffect, useRef, useState } from 'react';
import type { IssueRobotQrResponse } from '@bhops/shared';
import { formatQrId } from '@bhops/shared';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  result: IssueRobotQrResponse | null;
  robotSerial?: string | number;
}

// One-time plaintext token display. Once this modal closes, the token and
// the label PDF are gone forever — only the SHA-256 hash remains in the DB.

export function IssuedTokenModal({ open, onClose, result, robotSerial }: Props) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  // Close-guard: the token + label PDF are shown exactly once. Reflexive Esc
  // / X / backdrop dismiss has destroyed unrecoverable data in the past, so
  // route every close intent through a confirm dialog. `hasSaved` (token
  // copied OR PDF downloaded) bypasses the guard.
  const [closeGuardOpen, setCloseGuardOpen] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  // Timer for resetting the "복사 완료" → "토큰 복사" button label. Held in a
  // ref so we can cancel on unmount / re-copy. Without cleanup, a copy that
  // happens just before the user navigates away causes a setState on an
  // unmounted component (React warning) when the 2s timer fires.
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset internal state whenever the modal is (re-)opened.
  useEffect(() => {
    if (open) {
      setCopied(false);
      setCloseGuardOpen(false);
      setHasSaved(false);
    }
  }, [open]);

  // Cancel any pending copy-label timer on unmount.
  useEffect(
    () => () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    },
    [],
  );

  // Intercept Escape at the capture phase BEFORE Modal's internal keydown
  // handler runs (which would call onClose directly and bypass the guard).
  // Only active when the modal is open, the user hasn't saved yet, AND the
  // ConfirmDialog isn't itself open — otherwise Esc on the ConfirmDialog
  // would be swallowed here and the user would be trapped.
  useEffect(() => {
    if (!open || hasSaved || closeGuardOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setCloseGuardOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [open, hasSaved, closeGuardOpen]);

  if (!open || !result) return null;

  const token = result.plaintextToken;
  const label = robotSerial ? formatQrId(robotSerial, result.qr.rev) : null;

  function handleRequestClose(): void {
    if (hasSaved) {
      onClose();
      return;
    }
    setCloseGuardOpen(true);
  }

  async function copyToken(): Promise<void> {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setHasSaved(true);
      toast.success('토큰을 클립보드에 복사했습니다.');
      // Cancel any previous timer before scheduling a new one — a
      // double-click on copy would otherwise queue two timers, and
      // the first could flip `copied` back to false right after the
      // second sets it true.
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        copyTimerRef.current = null;
      }, 2000);
    } catch {
      toast.error('클립보드에 복사하지 못했습니다. 직접 복사해 주세요.');
    }
  }

  function downloadPdf(): void {
    if (!result?.labelPdfBase64) return;
    const byteChars = atob(result.labelPdfBase64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = robotSerial
      ? `qr-label-${robotSerial}-rev${result.qr.rev}.pdf`
      : `qr-label-${result.qr.id.slice(-8)}.pdf`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setHasSaved(true);
    toast.success('라벨 PDF를 다운로드했습니다.');
  }

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      title="QR 토큰이 발급되었습니다"
      width={540}
      footer={
        <Button type="button" onClick={handleRequestClose}>
          닫기
        </Button>
      }
    >
      <div
        role="alert"
        style={{
          padding: '10px 12px',
          marginBottom: 16,
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-warning-subtle)',
          border: '1px solid var(--bg-warning-subtle)',
          color: 'var(--fg-on-warning-tint)',
          fontSize: 13,
        }}
      >
        이 토큰과 라벨 PDF는 <strong>지금 한 번만</strong> 제공됩니다. 라벨을 다운로드하거나 토큰을
        복사한 뒤 이 창을 닫아 주세요.
      </div>

      {label && (
        <div className="kuun-label" style={{ marginBottom: 4 }}>
          Rev — <span className="kuun-mono">{label}</span>
        </div>
      )}

      <div className="kuun-label" style={{ marginTop: 12, marginBottom: 4 }}>
        QR에 담긴 포털 주소
      </div>
      <div
        style={{
          padding: 10,
          background: 'var(--bg-sunken)',
          borderRadius: 'var(--r-card)',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          wordBreak: 'break-all',
          userSelect: 'all',
          color: 'var(--fg-muted)',
        }}
      >
        {result.portalUrl}
      </div>

      <div className="kuun-label" style={{ marginTop: 12, marginBottom: 4 }}>
        토큰 원문
      </div>
      <div
        style={{
          padding: 12,
          background: 'var(--bg-sunken)',
          borderRadius: 'var(--r-card)',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          wordBreak: 'break-all',
          userSelect: 'all',
        }}
      >
        {token}
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="button" variant="secondary" onClick={copyToken}>
          {copied ? '복사 완료' : '토큰 복사'}
        </Button>
        {result.labelPdfBase64 ? (
          <Button type="button" onClick={downloadPdf}>
            라벨 PDF 다운로드
          </Button>
        ) : (
          <Button type="button" disabled>
            라벨 PDF 없음
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={closeGuardOpen}
        title="정말 닫으시겠습니까?"
        message={
          '토큰과 라벨 PDF는 이 창을 닫으면 다시 조회할 수 없습니다. ' +
          '토큰을 복사하거나 라벨 PDF를 받아 두지 않고 닫으면 ' +
          '다시 찾을 수 없습니다. 정말 닫으시겠습니까?'
        }
        confirmLabel="닫기"
        danger
        onConfirm={() => {
          setCloseGuardOpen(false);
          onClose();
        }}
        onCancel={() => setCloseGuardOpen(false)}
      />
    </Modal>
  );
}
